import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  getPortfolioPost,
  getPortfolioComments,
  addCommentToPortfolio,
  deletePortfolioComment,
  likePortfolioPost,
  unlikePortfolioPost,
  sharePortfolioPost,
  toggleFeatured,
} from "../../services/portfolioApi";
import "./style.css";

/**
 * COMPONENTE DE DETALHES DO PROJETO
 * Exibe informações completas de um projeto de portfólio
 */
const PortfolioDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => ({ ...state }));

  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [isLiked, setIsLiked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Carregar post
  useEffect(() => {
    loadPost();
    loadComments();
  }, [postId]);

  const loadPost = async () => {
    try {
      setLoading(true);
      const response = await getPortfolioPost(postId);
      if (response.success) {
        setPost(response.data);
        setIsLiked(
          response.data.likes && user
            ? response.data.likes.includes(user._id)
            : false
        );
      }
    } catch (error) {
      console.error("Erro ao carregar post:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const response = await getPortfolioComments(postId, { page: 1, limit: 50 });
      if (response.success) {
        setComments(response.data);
      }
    } catch (error) {
      console.error("Erro ao carregar comentários:", error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();

    if (!user) {
      navigate("/login");
      return;
    }

    if (!commentText.trim()) {
      alert("Comentário não pode estar vazio");
      return;
    }

    setCommentLoading(true);
    try {
      const response = await addCommentToPortfolio(
        postId,
        commentText,
        user.token
      );
      if (response.success) {
        setComments([...comments, response.data]);
        setCommentText("");
      }
    } catch (error) {
      console.error("Erro ao adicionar comentário:", error);
      alert("Erro ao adicionar comentário");
    } finally {
      setCommentLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Tem certeza que deseja deletar este comentário?")) {
      return;
    }

    try {
      await deletePortfolioComment(postId, commentId, user.token);
      setComments(comments.filter((c) => c._id !== commentId));
    } catch (error) {
      console.error("Erro ao deletar comentário:", error);
      alert("Erro ao deletar comentário");
    }
  };

  const handleLike = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    try {
      if (isLiked) {
        await unlikePortfolioPost(postId, user.token);
        setIsLiked(false);
        setPost((prev) => ({
          ...prev,
          likes: prev.likes.filter((id) => id !== user._id),
          portfolioData: {
            ...prev.portfolioData,
            metrics: {
              ...prev.portfolioData.metrics,
              likes: Math.max(0, prev.portfolioData.metrics.likes - 1),
            },
          },
        }));
      } else {
        await likePortfolioPost(postId, user.token);
        setIsLiked(true);
        setPost((prev) => ({
          ...prev,
          likes: [...(prev.likes || []), user._id],
          portfolioData: {
            ...prev.portfolioData,
            metrics: {
              ...prev.portfolioData.metrics,
              likes: prev.portfolioData.metrics.likes + 1,
            },
          },
        }));
      }
    } catch (error) {
      console.error("Erro ao curtir:", error);
      alert("Erro ao curtir");
    }
  };

  const handleShare = async (platform) => {
    if (!user) {
      navigate("/login");
      return;
    }

    try {
      await sharePortfolioPost(postId, platform, user.token);
      setShowShareMenu(false);
      alert(`Compartilhado em ${platform} com sucesso!`);
    } catch (error) {
      console.error("Erro ao compartilhar:", error);
      alert("Erro ao compartilhar");
    }
  };

  const handleToggleFeatured = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    try {
      const response = await toggleFeatured(postId, user.token);
      setPost((prev) => ({
        ...prev,
        portfolioData: {
          ...prev.portfolioData,
          featured: response.data.featured,
        },
      }));
    } catch (error) {
      console.error("Erro ao alternar destaque:", error);
      alert("Erro ao alternar destaque");
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="portfolio-detail">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Carregando projeto...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="portfolio-detail">
        <div className="error-container">
          <h2>Projeto não encontrado</h2>
          <button onClick={() => navigate(-1)}>Voltar</button>
        </div>
      </div>
    );
  }

  const portfolioData = post.portfolioData || {};
  const cardUser = post.user || {};
  const allImages = post.images || [];
  const currentImage = allImages[currentImageIndex] || null;

  return (
    <div className="portfolio-detail">
      <button className="back-btn" onClick={() => navigate(-1)}>
        ← Voltar
      </button>

      <div className="detail-container">
        {/* Gallery Section */}
        {allImages.length > 0 && (
          <div className="detail-gallery">
            <div className="main-image">
              <img src={currentImage} alt={portfolioData.projectName} />
            </div>
            {allImages.length > 1 && (
              <div className="thumbnail-list">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    className={`thumbnail ${idx === currentImageIndex ? "active" : ""}`}
                    onClick={() => setCurrentImageIndex(idx)}
                  >
                    <img src={img} alt={`Imagem ${idx + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Main Content */}
        <div className="detail-content">
          {/* Header */}
          <div className="detail-header">
            <h1>{portfolioData.projectName}</h1>
            {portfolioData.featured && (
              <span className="featured-badge">⭐ Destaque</span>
            )}
          </div>

          {/* User Info */}
          <div className="detail-user">
            <img
              src={cardUser.picture || "https://via.placeholder.com/50?text=User"}
              alt={cardUser.first_name}
              className="user-avatar"
            />
            <div className="user-info">
              <h3>
                {cardUser.first_name} {cardUser.last_name}
              </h3>
              {portfolioData.role && <p>{portfolioData.role}</p>}
            </div>
            {user && user._id === post.user._id && (
              <button
                className="edit-btn"
                onClick={() =>
                  navigate(`/edit-portfolio/${postId}`, { state: { post } })
                }
              >
                ✏️ Editar
              </button>
            )}
          </div>

          {/* Description */}
          <div className="detail-description">
            <h2>Sobre o Projeto</h2>
            <p>{portfolioData.description}</p>
          </div>

          {/* Project Details */}
          <div className="detail-info-grid">
            {portfolioData.category && (
              <div className="info-item">
                <label>Categoria</label>
                <span>{portfolioData.category.replace(/_/g, " ")}</span>
              </div>
            )}
            {portfolioData.client && (
              <div className="info-item">
                <label>Cliente</label>
                <span>{portfolioData.client}</span>
              </div>
            )}
            {portfolioData.startDate && (
              <div className="info-item">
                <label>Data de Início</label>
                <span>{formatDate(portfolioData.startDate)}</span>
              </div>
            )}
            {portfolioData.completedAt && (
              <div className="info-item">
                <label>Data de Conclusão</label>
                <span>{formatDate(portfolioData.completedAt)}</span>
              </div>
            )}
            {portfolioData.duration && (
              <div className="info-item">
                <label>Duração</label>
                <span>{portfolioData.duration}</span>
              </div>
            )}
            {portfolioData.teamSize && (
              <div className="info-item">
                <label>Tamanho da Equipe</label>
                <span>{portfolioData.teamSize} pessoas</span>
              </div>
            )}
            {portfolioData.budget?.amount && (
              <div className="info-item">
                <label>Orçamento</label>
                <span>
                  {portfolioData.budget.currency}{" "}
                  {portfolioData.budget.amount.toLocaleString()}
                </span>
              </div>
            )}
          </div>

          {/* Technologies */}
          {portfolioData.technologies && portfolioData.technologies.length > 0 && (
            <div className="detail-section">
              <h3>🛠️ Tecnologias Utilizadas</h3>
              <div className="tags-list">
                {portfolioData.technologies.map((tech, idx) => (
                  <span key={idx} className="tag">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Skills */}
          {portfolioData.skills && portfolioData.skills.length > 0 && (
            <div className="detail-section">
              <h3>💡 Habilidades Aplicadas</h3>
              <div className="tags-list">
                {portfolioData.skills.map((skill, idx) => (
                  <span key={idx} className="tag">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Links */}
          <div className="detail-links">
            {portfolioData.projectUrl && (
              <a
                href={portfolioData.projectUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="link-btn"
              >
                🌐 Visitar Projeto
              </a>
            )}
            {portfolioData.liveDemo && (
              <a
                href={portfolioData.liveDemo}
                target="_blank"
                rel="noopener noreferrer"
                className="link-btn demo"
              >
                🎮 Demo ao Vivo
              </a>
            )}
            {portfolioData.repositoryUrl && (
              <a
                href={portfolioData.repositoryUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="link-btn repo"
              >
                📂 Repositório
              </a>
            )}
            {portfolioData.caseStudyUrl && (
              <a
                href={portfolioData.caseStudyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="link-btn case"
              >
                📋 Case Study
              </a>
            )}
          </div>

          {/* Stats */}
          <div className="detail-stats">
            <div className="stat">
              <span className="stat-value">
                {portfolioData.metrics?.views || 0}
              </span>
              <span className="stat-label">Visualizações</span>
            </div>
            <div className="stat">
              <span className="stat-value">
                {portfolioData.metrics?.likes || 0}
              </span>
              <span className="stat-label">Curtidas</span>
            </div>
            <div className="stat">
              <span className="stat-value">
                {portfolioData.metrics?.shares || 0}
              </span>
              <span className="stat-label">Compartilhamentos</span>
            </div>
            <div className="stat">
              <span className="stat-value">{comments.length}</span>
              <span className="stat-label">Comentários</span>
            </div>
          </div>

          {/* Actions */}
          <div className="detail-actions">
            <button
              className={`action-btn like-btn ${isLiked ? "liked" : ""}`}
              onClick={handleLike}
            >
              {isLiked ? "❤️" : "🤍"} {isLiked ? "Curtido" : "Curtir"}
            </button>

            <div className="share-container">
              <button
                className="action-btn share-btn"
                onClick={() => setShowShareMenu(!showShareMenu)}
              >
                📤 Compartilhar
              </button>
              {showShareMenu && (
                <div className="share-menu">
                  <button onClick={() => handleShare("facebook")}>
                    📘 Facebook
                  </button>
                  <button onClick={() => handleShare("twitter")}>
                    𝕏 Twitter
                  </button>
                  <button onClick={() => handleShare("linkedin")}>
                    💼 LinkedIn
                  </button>
                  <button onClick={() => handleShare("whatsapp")}>
                    💬 WhatsApp
                  </button>
                  <button onClick={() => handleShare("copy")}>
                    📋 Copiar Link
                  </button>
                </div>
              )}
            </div>

            {user && user._id === post.user._id && (
              <button
                className="action-btn feature-btn"
                onClick={handleToggleFeatured}
              >
                {portfolioData.featured ? "⭐" : "☆"}{" "}
                {portfolioData.featured ? "Remover Destaque" : "Destacar"}
              </button>
            )}
          </div>

          {/* Comments Section */}
          <div className="detail-comments">
            <h2>Comentários ({comments.length})</h2>

            {user && (
              <form onSubmit={handleAddComment} className="comment-form">
                <img
                  src={user.picture || "https://via.placeholder.com/40?text=User"}
                  alt={user.first_name}
                  className="user-avatar"
                />
                <div className="comment-input-group">
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Deixe seu comentário..."
                    rows={3}
                  />
                  <button
                    type="submit"
                    disabled={!commentText.trim() || commentLoading}
                  >
                    {commentLoading ? "Enviando..." : "Comentar"}
                  </button>
                </div>
              </form>
            )}

            <div className="comments-list">
              {comments.length === 0 ? (
                <p className="no-comments">Nenhum comentário ainda.</p>
              ) : (
                comments.map((comment) => (
                  <div key={comment._id} className="comment-item">
                    <img
                      src={
                        comment.commentBy?.picture ||
                        "https://via.placeholder.com/40?text=User"
                      }
                      alt={comment.commentBy?.first_name}
                      className="user-avatar"
                    />
                    <div className="comment-content">
                      <h4>{comment.commentBy?.first_name}</h4>
                      <p>{comment.text}</p>
                      <small>
                        {new Date(comment.createdAt).toLocaleDateString("pt-BR")}
                      </small>
                    </div>
                    {user &&
                      (user._id === comment.commentBy?._id ||
                        user._id === post.user._id) && (
                        <button
                          className="delete-comment-btn"
                          onClick={() => handleDeleteComment(comment._id)}
                        >
                          🗑️
                        </button>
                      )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioDetail;
